import React from 'react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ZapIcon, TrendingUpIcon, GaugeIcon, BanknoteIcon, CalendarIcon, DownloadIcon } from 'lucide-react';
import { formatZAR } from '../hooks/useMavisData';
const dailyData = [{
  day: 'Mon',
  value: 320000
}, {
  day: 'Tue',
  value: 345000
}, {
  day: 'Wed',
  value: 310000
}, {
  day: 'Thu',
  value: 380000
}, {
  day: 'Fri',
  value: 365000
}, {
  day: 'Sat',
  value: 420000
}, {
  day: 'Sun',
  value: 390000
}];
const hourlyData = Array.from({
  length: 24
}, (_, i) => {
  const hour = i;
  let value = 15000 + Math.random() * 5000;
  // Morning peak
  if (hour >= 6 && hour <= 9) value += 10000 + Math.random() * 5000;
  // Evening peak
  if (hour >= 17 && hour <= 20) value += 20000 + Math.random() * 10000;
  return {
    hour: `${hour.toString().padStart(2, '0')}:00`,
    value: Math.round(value),
    isPeak: hour >= 17 && hour <= 20
  };
});
const areaData = [{
  name: 'Soweto',
  value: 45000
}, {
  name: 'Tembisa',
  value: 32000
}, {
  name: 'Khayelitsha',
  value: 28000
}, {
  name: 'Mamelodi',
  value: 24000
}, {
  name: 'Umlazi',
  value: 21000
}, {
  name: 'Alexandra',
  value: 15000
}];
const timeOfUseData = [{
  name: 'Peak',
  value: 35,
  color: '#E07020'
}, {
  name: 'Standard',
  value: 40,
  color: '#0D4F5C'
}, {
  name: 'Off-Peak',
  value: 25,
  color: '#8DB43E'
}];
const topConsumers = [{
  rank: 1,
  id: '04-8001-1001-10',
  name: 'Eastgate Shopping Centre',
  area: 'Bedfordview',
  dailyAvg: 6180,
  monthlyTotal: 185400,
  history: [6100, 6200, 6150, 6300, 6250, 6180, 6200]
}, {
  rank: 2,
  id: '04-8002-2002-20',
  name: 'Toyota SA Manufacturing',
  area: 'Prospecton',
  dailyAvg: 11400,
  monthlyTotal: 342000,
  history: [11200, 11500, 11300, 11600, 11450, 11400, 11500]
}, {
  rank: 3,
  id: '04-8004-4004-40',
  name: 'Sappi Ngodwana Mill',
  area: 'Mpumalanga',
  dailyAvg: 17333,
  monthlyTotal: 520000,
  history: [17200, 17400, 17300, 17500, 17350, 17333, 17400]
}, {
  rank: 4,
  id: '04-8007-7007-70',
  name: 'ArcelorMittal Vanderbijlpark',
  area: 'Vanderbijlpark',
  dailyAvg: 29666,
  monthlyTotal: 890000,
  history: [29500, 29800, 29600, 29900, 29700, 29666, 29800]
}, {
  rank: 5,
  id: '04-8006-6006-60',
  name: 'Sandton City Complex',
  area: 'Sandton',
  dailyAvg: 9166,
  monthlyTotal: 275000,
  history: [9100, 9200, 9150, 9300, 9250, 9166, 9200]
}, {
  rank: 6,
  id: '04-8003-3003-30',
  name: 'Menlyn Park Shopping Centre',
  area: 'Pretoria',
  dailyAvg: 7016,
  monthlyTotal: 210500,
  history: [7000, 7100, 7050, 7200, 7150, 7016, 7100]
}];
const COLORS = ['#0D4F5C', '#EAB308', '#8DB43E', '#E07020', '#9AADCC', '#4b5563'];
export function ConsumptionAnalytics() {
  return <div className="p-8 max-w-7xl mx-auto animate-in fade-in duration-500" data-id="element-795">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4" data-id="element-796">
        <div data-id="element-797">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-798">
            Consumption Analytics
          </h1>
          <p className="text-gray-500" data-id="element-799">
            Detailed energy consumption patterns and trends across the meter
            network.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-800">
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-none px-3 py-2 shadow-sm" data-id="element-801">
            <CalendarIcon className="w-4 h-4 text-gray-500" data-id="element-802" />
            <select className="bg-transparent text-sm font-medium text-gray-700 outline-none cursor-pointer" data-id="element-803">
              <option data-id="element-804">Last 7 Days</option>
              <option data-id="element-805">Last 30 Days</option>
              <option data-id="element-806">This Quarter</option>
              <option data-id="element-807">Year to Date</option>
            </select>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-mavis-teal text-white rounded-none hover:bg-mavis-teal/90 transition-colors shadow-sm font-medium" data-id="element-808">
            <DownloadIcon className="w-4 h-4" data-id="element-809" />
            Export Data
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-810">
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-811">
          <div className="p-3 bg-mavis-teal/10 text-mavis-teal rounded-none" data-id="element-812">
            <ZapIcon className="w-5 h-5" data-id="element-813" />
          </div>
          <div data-id="element-814">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-815">
              Total Consumption
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-816">
              2,847,350{' '}
              <span className="text-sm font-sans text-gray-500" data-id="element-817">kWh</span>
            </p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-818">
          <div className="p-3 bg-mavis-gold/10 text-mavis-gold rounded-none" data-id="element-819">
            <TrendingUpIcon className="w-5 h-5" data-id="element-820" />
          </div>
          <div data-id="element-821">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-822">
              Peak Demand
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-823">
              42.8 <span className="text-sm font-sans text-gray-500" data-id="element-824">MVA</span>
            </p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-825">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-none" data-id="element-826">
            <GaugeIcon className="w-5 h-5" data-id="element-827" />
          </div>
          <div data-id="element-828">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-829">
              Avg Daily per Meter
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-830">
              14.2 <span className="text-sm font-sans text-gray-500" data-id="element-831">kWh</span>
            </p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-832">
          <div className="p-3 bg-mavis-green/10 text-mavis-green rounded-none" data-id="element-833">
            <BanknoteIcon className="w-5 h-5" data-id="element-834" />
          </div>
          <div data-id="element-835">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-836">
              Estimated Cost
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-837">
              {formatZAR(4271025)}
            </p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-838">
        {/* Daily Consumption Trend */}
        <div className="bg-white p-6 rounded-none shadow-sm border border-gray-200" data-id="element-839">
          <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-840">
            Daily Consumption Trend
          </h2>
          <div className="h-80" data-id="element-841">
            <ResponsiveContainer width="100%" height="100%" data-id="element-842">
              <AreaChart data={dailyData} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0
            }} data-id="element-843">

                <defs data-id="element-844">
                  <linearGradient id="colorDaily" x1="0" y1="0" x2="0" y2="1" data-id="element-845">
                    <stop offset="5%" stopColor="#0D4F5C" stopOpacity={0.3} data-id="element-846" />
                    <stop offset="95%" stopColor="#0D4F5C" stopOpacity={0} data-id="element-847" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" data-id="element-848" />

                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} dy={10} data-id="element-849" />

                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} tickFormatter={val => `${val / 1000}k`} data-id="element-850" />

                <Tooltip formatter={(value: number) => [`${value.toLocaleString()} kWh`, 'Consumption']} contentStyle={{
                borderRadius: '0',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-851" />

                <Area type="monotone" dataKey="value" stroke="#0D4F5C" strokeWidth={2} fillOpacity={1} fill="url(#colorDaily)" data-id="element-852" />

              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Hourly Load Profile */}
        <div className="bg-white p-6 rounded-none shadow-sm border border-gray-200" data-id="element-853">
          <div className="flex justify-between items-center mb-6" data-id="element-854">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-855">
              Hourly Load Profile
            </h2>
            <div className="flex items-center gap-4 text-xs" data-id="element-856">
              <div className="flex items-center gap-1.5" data-id="element-857">
                <div className="w-3 h-3 bg-mavis-teal rounded-none" data-id="element-858"></div>
                <span className="text-gray-600" data-id="element-859">Off-Peak</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-860">
                <div className="w-3 h-3 bg-mavis-orange rounded-none" data-id="element-861"></div>
                <span className="text-gray-600" data-id="element-862">Peak (17:00-20:00)</span>
              </div>
            </div>
          </div>
          <div className="h-80" data-id="element-863">
            <ResponsiveContainer width="100%" height="100%" data-id="element-864">
              <BarChart data={hourlyData} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0
            }} data-id="element-865">

                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" data-id="element-866" />

                <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{
                fill: '#6b7280',
                fontSize: 10
              }} interval={2} dy={10} data-id="element-867" />

                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} tickFormatter={val => `${val / 1000}k`} data-id="element-868" />

                <Tooltip formatter={(value: number) => [`${value.toLocaleString()} kWh`, 'Load']} contentStyle={{
                borderRadius: '0',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} cursor={{
                fill: '#f3f4f6'
              }} data-id="element-869" />

                <Bar dataKey="value" radius={[0, 0, 0, 0]} data-id="element-870">
                  {hourlyData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.isPeak ? '#E07020' : '#0D4F5C'} data-id="element-871" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Consumption by Area */}
        <div className="bg-white p-6 rounded-none shadow-sm border border-gray-200" data-id="element-872">
          <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-873">
            Consumption by Area (kWh)
          </h2>
          <div className="h-80" data-id="element-874">
            <ResponsiveContainer width="100%" height="100%" data-id="element-875">
              <BarChart data={areaData} layout="vertical" margin={{
              top: 5,
              right: 30,
              left: 40,
              bottom: 5
            }} data-id="element-876">

                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f0f0f0" data-id="element-877" />

                <XAxis type="number" tick={{
                fill: '#6b7280',
                fontSize: 12
              }} axisLine={false} tickLine={false} tickFormatter={val => `${val / 1000}k`} data-id="element-878" />

                <YAxis dataKey="name" type="category" tick={{
                fill: '#374151',
                fontSize: 12,
                fontWeight: 500
              }} axisLine={false} tickLine={false} data-id="element-879" />

                <Tooltip formatter={(value: number) => [`${value.toLocaleString()} kWh`, 'Consumption']} cursor={{
                fill: '#f3f4f6'
              }} contentStyle={{
                borderRadius: '0',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-880" />

                <Bar dataKey="value" radius={[0, 0, 0, 0]} data-id="element-881">
                  {areaData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-882" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Time-of-Use Distribution */}
        <div className="bg-white p-6 rounded-none shadow-sm border border-gray-200" data-id="element-883">
          <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-884">
            Time-of-Use Distribution
          </h2>
          <div className="h-80 flex items-center justify-center" data-id="element-885">
            <ResponsiveContainer width="100%" height="100%" data-id="element-886">
              <PieChart data-id="element-887">
                <Pie data={timeOfUseData} cx="50%" cy="50%" innerRadius={80} outerRadius={120} paddingAngle={5} dataKey="value" data-id="element-888">

                  {timeOfUseData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-889" />)}
                </Pie>
                <Tooltip formatter={value => [`${value}%`, 'Share']} contentStyle={{
                borderRadius: '0',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-890" />

                <Legend verticalAlign="bottom" height={36} iconType="circle" data-id="element-891" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top Consumers Table */}
      <div className="bg-white rounded-none shadow-sm border border-gray-200 overflow-hidden" data-id="element-892">
        <div className="p-6 border-b border-gray-200 bg-gray-50/50" data-id="element-893">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-894">Top Consumers</h2>
        </div>
        <div className="overflow-x-auto" data-id="element-895">
          <table className="w-full text-left border-collapse" data-id="element-896">
            <thead data-id="element-897">
              <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider border-b border-gray-200" data-id="element-898">
                <th className="p-4 font-medium w-16 text-center" data-id="element-899">Rank</th>
                <th className="p-4 font-medium" data-id="element-900">Meter ID</th>
                <th className="p-4 font-medium" data-id="element-901">Customer</th>
                <th className="p-4 font-medium" data-id="element-902">Area</th>
                <th className="p-4 font-medium text-right" data-id="element-903">Daily Avg (kWh)</th>
                <th className="p-4 font-medium text-right" data-id="element-904">
                  Monthly Total (kWh)
                </th>
                <th className="p-4 font-medium w-24" data-id="element-905">Trend</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-906">
              {topConsumers.map(consumer => <tr key={consumer.id} className="hover:bg-gray-50 transition-colors group" data-id="element-907">

                  <td className="p-4 text-center" data-id="element-908">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-700 font-bold text-xs" data-id="element-909">
                      {consumer.rank}
                    </span>
                  </td>
                  <td className="p-4 font-mono font-medium text-gray-900" data-id="element-910">
                    {consumer.id}
                  </td>
                  <td className="p-4 font-medium text-gray-900" data-id="element-911">
                    {consumer.name}
                  </td>
                  <td className="p-4 text-sm text-gray-600" data-id="element-912">{consumer.area}</td>
                  <td className="p-4 text-right font-mono font-bold text-mavis-teal" data-id="element-913">
                    {consumer.dailyAvg.toLocaleString()}
                  </td>
                  <td className="p-4 text-right font-mono font-medium text-gray-900" data-id="element-914">
                    {consumer.monthlyTotal.toLocaleString()}
                  </td>
                  <td className="p-4" data-id="element-915">
                    <div className="h-6 w-16 opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-916">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-917">
                        <LineChart data={consumer.history.map((val, i) => ({
                      val,
                      i
                    }))} data-id="element-918">

                          <YAxis domain={['dataMin - 100', 'dataMax + 100']} hide data-id="element-919" />

                          <Line type="monotone" dataKey="val" stroke="#0D4F5C" strokeWidth={2} dot={false} isAnimationActive={false} data-id="element-920" />

                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}